import { dedupe, guuid, isArray, isBoolean, isNeString, isNumber } from '@aretheyhappy/utils';
/**
 * Type for individual items that appear in MultiStepFilter
 */
var MULTI_STEP_FILTER_TYPES;
(function (MULTI_STEP_FILTER_TYPES) {
    MULTI_STEP_FILTER_TYPES["GROUP"] = "GROUP";
    MULTI_STEP_FILTER_TYPES["ITEM"] = "ITEM";
})(MULTI_STEP_FILTER_TYPES || (MULTI_STEP_FILTER_TYPES = {}));
/**
 * Modes supported by MultiStepFilter
 */
var MUTLI_STEP_FILTER_MODES;
(function (MUTLI_STEP_FILTER_MODES) {
    // No selection is allowed (display only)
    MUTLI_STEP_FILTER_MODES["NO_SELECT"] = "NO_SELECT";
    // Only one element can be selected at a time (no checkmarks)
    MUTLI_STEP_FILTER_MODES["SINGLE"] = "SINGLE";
    // Multi selection (checkmarks)
    MUTLI_STEP_FILTER_MODES["MULTI"] = "MULTI";
})(MUTLI_STEP_FILTER_MODES || (MUTLI_STEP_FILTER_MODES = {}));
/**
 * Sorts supported by MultiStepFilter
 */
var MULTI_STEP_FILTER_SORT;
(function (MULTI_STEP_FILTER_SORT) {
    MULTI_STEP_FILTER_SORT["ASC"] = "asc";
    MULTI_STEP_FILTER_SORT["DESC"] = "desc";
    MULTI_STEP_FILTER_SORT["NO_SORT"] = "no_sort";
})(MULTI_STEP_FILTER_SORT || (MULTI_STEP_FILTER_SORT = {}));
/**
 * Given an string array and a list of MultiStepFilterItems, recursively add
 * all values to that array.
 * @param values
 * @param list
 * @returns
 */
const addValuesToArray = function (values, list) {
    for (const item of list) {
        // Only add value if its not empty
        if (isNeString(item.value))
            values.push(item.value);
        if (item.isGroup) {
            addValuesToArray(values, item.children);
        }
    }
    return values;
};
/**
 * Given an string array and a list of MultiStepFilterItems, recursively add
 * all values that are leaf nodes to that array.
 * @param values
 * @param list
 * @returns
 */
const addLeafValuesToArray = function (values, list) {
    for (const item of list) {
        if (!item.isGroup && isNeString(item.value))
            values.push(item.value);
        if (item.isGroup) {
            addLeafValuesToArray(values, item.children);
        }
    }
    return values;
};
/**
 * Definition class for items that will appear in a MultiStepFilter
 */
class MultiStepFilterItem {
    constructor(value = '') {
        this.uid = guuid();
        // Value for this item when selected
        this.value = '';
        // Label to show on row
        this.label = '';
        // Subtitle to show under row
        this.subtitle = '';
        // Badge label to show
        this.badge = '';
        // Visual style for badge
        this.badge_style = '';
        // Array of MultiStepFilterItem's that are a child of this one
        this.children = [];
        // Parent item
        this.parent = undefined;
        // If this item can be configured. Only used by MultiStepFilterConfigList
        this.hasConfig = false;
        // Set the sort direction for child items
        this.sort_dir = MULTI_STEP_FILTER_SORT.ASC;
        // Set the always_selectable option for a filter item group
        this.always_selectable = false;
        // If true, this item is kept out of the normal sort and shown ahead of the
        // rest of the list for its section
        this.pinned = false;
        // Any additional metadata can be added as needed
        this.metadata = {};
        // Error message to show on status badge
        this.error_message = '';
        this.value = value;
    }
    setErrorMessage(value) { this.error_message = value; return this; }
    setValue(value) { this.value = value; return this; }
    setLabel(value) { this.label = value; return this; }
    setSubtitle(value) { this.subtitle = value; return this; }
    setBadge(value) { this.badge = value; return this; }
    setBadgeStyle(value) { this.badge_style = value; return this; }
    setHasConfig(value) { this.hasConfig = value; return this; }
    setSortDir(value) {
        if (!Object.values(MULTI_STEP_FILTER_SORT).includes(value))
            return this;
        this.sort_dir = value;
        return this;
    }
    setMetadata(value) { this.metadata = value; return this; }
    setChildren(value) {
        for (const child of value) {
            // These changes are needed for storybook
            Object.defineProperty(child, 'parent', {
                value: this,
                enumerable: false,
                writable: true,
                configurable: true // allow deletion/redefinition
            });
        }
        this.children = value;
        return this;
    }
    setAlwaysSelectable(value) { this.always_selectable = value; return this; }
    setPinned(value) { this.pinned = value; return this; }
    get type() {
        return this.children.length === 0 ? MULTI_STEP_FILTER_TYPES.ITEM : MULTI_STEP_FILTER_TYPES.GROUP;
    }
    get isGroup() {
        return this.type === MULTI_STEP_FILTER_TYPES.GROUP;
    }
    get allValues() {
        return [this.value, ...this.allDescendantValues];
    }
    get allLeafValues() {
        if (this.isGroup)
            return this.allDescendantLeafValues;
        return [this.value];
    }
    get allDescendantValues() {
        return addValuesToArray([], this.children);
    }
    get allDescendantLeafValues() {
        return addLeafValuesToArray([], this.children);
    }
    get hasErrorMessage() {
        return isNeString(this.error_message);
    }
    get breadcrumbs() {
        const crumbs = [{
                label: this.label,
                children: this.children,
                sort_dir: this.sort_dir,
            }];
        let parent = this.parent;
        while (parent !== undefined) {
            crumbs.unshift({
                label: parent.label,
                children: parent.children,
                sort_dir: parent.sort_dir,
            });
            parent = parent.parent;
        }
        return crumbs;
    }
    /**
     * Recursively search children and descendants for matches.
     * Also adds `this` to list if it matches.
     * @param {string} qstring
     * @returns
     */
    search(qstring) {
        const matches = [];
        if (this.label.toLowerCase().includes(qstring.toLowerCase()) ||
            this.subtitle.toLowerCase().includes(qstring.toLowerCase()))
            matches.push(this);
        if (this.isGroup) {
            for (const child of this.children) {
                matches.push(...child.search(qstring));
            }
        }
        return matches;
    }
    /**
     * Check if this item should show as checked
     * @param {string[]} selectedItems
     */
    isSelected(selectedItems) {
        // Non groups must be in the list
        if (!this.isGroup)
            return selectedItems.includes(this.value);
        // Groups must have all sub items
        for (const id of this.allDescendantValues) {
            if (!selectedItems.includes(id))
                return false;
        }
        return true;
    }
    /**
     * Check if some but not all items are checked
     * @param {string[]} selectedItems
     */
    isPartialSelected(selectedItems) {
        // Not group, so cant be partial
        if (!this.isGroup)
            return false;
        // All selected, so not partial
        if (this.isSelected(selectedItems))
            return false;
        // Any single sub item is clicked, so return true
        for (const id of this.allDescendantValues) {
            if (selectedItems.includes(id))
                return true;
        }
        return false;
    }
    /**
     * Check if parent items should be added to selection
     * Make sure parent groups get checked if they need to (all children get checked)
     * @param {*} selectedItems
     */
    checkParentSelection(selectedItems) {
        let parent = this.parent;
        while (parent !== undefined) {
            // All parent sub items are in the list but the parent is not. So add parent
            if (parent.isSelected(selectedItems) && !selectedItems.includes(parent.value)) {
                selectedItems.push(parent.value);
            }
            parent = parent.parent;
        }
        return selectedItems;
    }
    /**
     * Recalculate the checked state based on the sent MultiFilterEngine
     * @param engine
     * @returns
     */
    calculateCheckedState(engine) {
        // When keepPartialGroups is enabled and this is a group, check if the group value itself is in the selection
        if (engine.keepPartialGroups && this.isGroup) {
            return engine.selected.includes(this.value);
        }
        // Default behavior: checked if partially or fully selected
        return this.isPartialSelected(engine.selected) || this.isSelected(engine.selected);
    }
    /**
     * Get the list of checked values depending on the state of this
     * item.
     * @param checked
     */
    getCheckedList(checked, engine) {
        // Add all descendants to checked list on check
        if (checked) {
            let checkedItems = engine.selected;
            if (engine.isolateSelection) {
                checkedItems.push(this.value);
            }
            else {
                checkedItems.push(...this.allValues);
                // Make sure parent groups get checked if they need to (all children get checked)
                checkedItems = this.checkParentSelection(checkedItems);
            }
            // Ensure no duplicates
            checkedItems = dedupe(checkedItems);
            return checkedItems;
        }
        // Remove this item and all descendants from checked list
        else {
            // When keepPartialGroups is enabled and this is a group, only remove the group value itself
            // but keep the children that are still checked
            if (engine.keepPartialGroups && this.isGroup) {
                return engine.selected.filter(id => id !== this.value);
            }
            else {
                return engine.selected.filter(id => !this.allValues.includes(id));
            }
        }
    }
    /**
     * If the checkmark should be shown
     * @param engine
     * @returns
     */
    showCheckmark(engine) {
        // No value and no children, then nothing to select
        if (!isNeString(this.value) && !this.isGroup)
            return false;
        // We are in multi mode.
        // AND item is a group and group selection or always selectable is enabled for this item
        //     OR Item is a leaf
        return engine.isMultiMode &&
            ((this.isGroup && (engine.allowGroupSelection || this.always_selectable)) || !this.isGroup);
    }
    /**
     * Can the user click on this item anywhere to select it
     * @returns
     */
    canClickRowForSelect(engine) {
        // No value and no children, then nothing to select
        if (!isNeString(this.value) && !this.isGroup)
            return false;
        // We are in multi mode and have groups with no checkmarks or leaf.
        // We are in single selection mode and have groups that cant be selected.
        return (engine.isMultiMode && ((this.isGroup && !this.showCheckmark(engine)) || !this.isGroup)) ||
            (!engine.isMultiMode && this.isGroup && (!engine.allowGroupSelection || !this.always_selectable));
    }
    /**
     * Returns true if this item can be single selected
     * @param engine
     */
    canSingleSelect(engine) {
        return engine.mode === MUTLI_STEP_FILTER_MODES.SINGLE && !engine.isRestrictedItem(this) &&
            ((this.isGroup && (engine.allowGroupSelection || this.always_selectable)) ||
                (!this.isGroup));
    }
    /**
     * Return true of false if we can toggle checked state
     * @param engine
     * @returns
     */
    canToggleCheckedState(engine, checked) {
        if (checked) {
            // We can always uncheck if no minimum.
            if (engine.min === 0)
                return true;
            // We can check if it would not go under count
            return engine.selectionCount - this.getBadgeCount(engine) >= engine.min;
        }
        // We can always interact if no max
        if (engine.max === 0)
            return true;
        // If we can include groups. We can check if it would not go over count
        if (engine.includeGroups) {
            return engine.selectionCount + this.allValues.length <= engine.max;
        }
        // If we can only select leafs. Same thing, but not including groups.
        return engine.selectionCount + this.allLeafValues.length <= engine.max;
    }
    /**
     * Calculate badge count for this item
     * @param engine
     */
    getBadgeCount(engine) {
        let count = 0;
        const childValues = engine.includeGroups ? this.allValues : this.allLeafValues;
        for (const value of childValues) {
            if (engine.selected.includes(value))
                count++;
        }
        return count;
    }
    /**
     * Sort children and return them
     */
    getSortedChildren() {
        return MultiStepFilterItem.sortWithPinned(this.children, this.sort_dir);
    }
    /**
     * Sort a list of items by label, respecting the given sort direction, while
     * keeping pinned items out of the normal sort. Pinned items are sorted
     * amongst themselves using the same rules, then placed ahead of the rest
     * of the (also sorted) list, rather than being interleaved with it.
     * @param items
     * @param sort_dir
     */
    static sortWithPinned(items, sort_dir = MULTI_STEP_FILTER_SORT.ASC) {
        if (sort_dir === MULTI_STEP_FILTER_SORT.NO_SORT)
            return items;
        const sortByLabel = (list) => {
            const sorted = [...list].sort((a, b) => a.label.localeCompare(b.label));
            if (sort_dir === MULTI_STEP_FILTER_SORT.DESC)
                sorted.reverse();
            return sorted;
        };
        return [
            ...sortByLabel(items.filter(item => item.pinned)),
            ...sortByLabel(items.filter(item => !item.pinned)),
        ];
    }
}
;
/**
 * This Engine powers selection state and rules for a MultiFilter or a MultiStepFilter
 */
class MultiFilterEngine {
    constructor() {
        // Array of MultiStepFilterItem's
        this.items = [];
        // Values of currently selected items (dont include group values
        // if includeGroups=false)
        this.selected = [];
        // Selection reported to consumers after engine properties have been
        // taken into account. Is recomputed when calling updateSelection.
        this.selectionOutput = [];
        // Items that are not allowed to be selected because they are restricted
        // Only for single selection mode
        this.restrictedItems = [];
        // Max amount of items that can be selected. 0 is no limit
        this.max = 0;
        // Minimum amount of items that can be selected. 0 is no limit
        this.min = 0;
        // If groups should be included in selection
        this.includeGroups = true;
        // Show Checkmarks on groups or allow clicking groups. If all groups should
        // be clicked set this to true, otherwise use the always_selectable prop for filter item
        this.allowGroupSelection = false;
        // Selection mode
        this.mode = MUTLI_STEP_FILTER_MODES.NO_SELECT;
        // If true, parent groups remain checked even when only some children are selected
        this.keepPartialGroups = false;
        // if true, selection will only include the item selected, not the children of a group.
        this.isolateSelection = false;
    }
    get isMultiMode() {
        return this.mode === MUTLI_STEP_FILTER_MODES.MULTI;
    }
    get selectionCount() {
        return this.selectionOutput.length;
    }
    /**
     * Returns a list of all group values
     */
    get allGroupValues() {
        const addValuesToArray = function (values, list) {
            for (const item of list) {
                if (item.isGroup) {
                    values.push(item.value);
                    addValuesToArray(values, item.children);
                }
            }
            return values;
        };
        const groupIds = addValuesToArray([], this.items);
        return groupIds;
    }
    /**
     * Set the list of items managed by this engine
     * @param items
     * @param silent
     * @returns
     */
    setItems(items, silent = true) {
        if (!isArray(items))
            return this;
        this.items = items;
        if (!silent)
            this.updateSelection(this.selected);
        return this;
    }
    /**
     * Set the list of selected items. This should be a string array
     * and relates directly to the `value` on MultiStepFilterItems.
     *
     * Only call this on initial setup, otherwise call `updateSelection`
     * @param items
     * @returns
     */
    setSelectedItems(items) {
        if (!isArray(items))
            return this;
        this.selected = items;
        this.selectionOutput = items;
        return this;
    }
    /**
     * Set the list of items that is not allowed to be selected.
     * @param items
     * @returns
     */
    setRestrictedItems(items) {
        if (!isArray(items))
            return this;
        this.restrictedItems = items;
        return this;
    }
    /**
     * Set the maximum number of items that can be selected
     * @param max
     * @returns
     */
    setMax(max) {
        if (!isNumber(max))
            return this;
        this.max = max;
        return this;
    }
    /**
     * Set the minimum number of items that can be selected
     * @param max
     * @returns
     */
    setMin(min) {
        if (!isNumber(min))
            return this;
        this.min = min;
        return this;
    }
    /**
     * Set if group values should be included in the selection or not
     * @param value
     * @param silent
     * @returns
     */
    setIncludeGroups(value, silent = true) {
        if (!isBoolean(value))
            return this;
        this.includeGroups = value;
        if (!silent)
            this.updateSelection(this.selected);
        return this;
    }
    /**
     * Show Checkmarks on groups or allow clicking groups
     * @param value
     * @returns
     */
    setAllowGroupSelection(value) {
        if (!isBoolean(value))
            return this;
        this.allowGroupSelection = value;
        return this;
    }
    /**
     * Set the mode that is currently configured for this MultiFilter
     * @param mode
     * @returns
     */
    setMode(mode) {
        if (!Object.values(MUTLI_STEP_FILTER_MODES).includes(mode))
            return this;
        this.mode = mode;
        return this;
    }
    /**
     * Set if partial groups should be counted in selection
     * @param value
     * @param silent
     * @returns
     */
    setKeepPartialGroups(value, silent = true) {
        if (!isBoolean(value))
            return this;
        this.keepPartialGroups = value;
        if (this.isolateSelection)
            this.keepPartialGroups = true;
        if (!silent)
            this.updateSelection(this.selected);
        return this;
    }
    /**
     * Set if selecting a group should include the group value in the selection, or just the children values.
     *
     * Here we also enforce keep partial groups to be true, because it would not make sense to have isolateSelection enabled
     *  but not keep partial groups, as the group value is what indicates the group is selected.
     * @param value
     * @param silent
     * @returns
     */
    setIsolateSelection(value) {
        if (!isBoolean(value))
            return this;
        this.isolateSelection = value;
        this.keepPartialGroups = true;
        return this;
    }
    /**
     * Update and recompute the selected elements
     * @param value
     */
    updateSelection(selection) {
        // Check if any items have a missing child checked (so the group needs to be removed)
        // Skip this logic if keepPartialGroups is enabled
        const valuesToRemove = [];
        if (!this.keepPartialGroups) {
            const verifyChildren = (children) => {
                for (const child of children) {
                    if (child.isGroup) {
                        // Value should be removed from list because not all children are checked
                        if (selection.includes(child.value) && !child.isSelected(selection))
                            valuesToRemove.push(child.value);
                        verifyChildren(child.children);
                    }
                }
            };
            verifyChildren(this.items);
        }
        // Make sure invalid checked groups are removed
        this.selected = selection.filter(id => !valuesToRemove.includes(id));
        // Compute real selection for consumers
        this.selectionOutput = this.includeGroups ? this.selected : this.selected.filter(id => !this.allGroupValues.includes(id));
        return this.selectionOutput;
    }
    /**
     * Returns the values "select all" toggles for a list of items. With group selection
     * this is every descendant value (groups and leafs), otherwise only descendant leafs.
     * @param items
     */
    getSelectAllValues(items) {
        const values = [];
        for (const item of items) {
            values.push(...(this.allowGroupSelection ? item.allValues : item.allLeafValues));
        }
        return dedupe(values.filter(value => isNeString(value)));
    }
    /**
     * Returns the selection after checking or unchecking "select all" for a list of items
     * @param items
     * @param checked
     */
    getSelectAllCheckedList(items, checked) {
        const values = this.getSelectAllValues(items);
        if (!checked)
            return this.selected.filter(id => !values.includes(id));
        let checkedItems = [...this.selected, ...values];
        // Make sure parent groups get checked if they need to (all children get checked).
        // All items have the same parent, so just check the first one. Leaf only mode
        // never adds group values.
        if (this.allowGroupSelection && items.length > 0) {
            checkedItems = items[0].checkParentSelection(checkedItems);
        }
        return dedupe(checkedItems);
    }
    /**
     * Returns if every value "select all" toggles for a list of items is selected
     * @param items
     */
    isAllSelected(items) {
        const values = this.getSelectAllValues(items);
        return values.length > 0 && values.every(id => this.selected.includes(id));
    }
    /**
     * Returns if any value "select all" toggles for a list of items is selected
     * @param items
     */
    isAnySelected(items) {
        return this.getSelectAllValues(items).some(id => this.selected.includes(id));
    }
    /**
     * Returns if "select all" can be used for a list of items
     * @param items
     * @param qstring Current search query. Select all is not available while searching
     */
    canSelectAll(items, qstring = '') {
        return this.isMultiMode && this.max === 0 && this.min === 0 && qstring.length === 0 &&
            this.getSelectAllValues(items).length > 0;
    }
    /**
     * Returns if the current selection can be cleared. Not available in no-select mode,
     * when a minimum selection is required or when nothing is selected.
     */
    canClearSelection() {
        return this.mode !== MUTLI_STEP_FILTER_MODES.NO_SELECT && this.min === 0 && this.selected.length > 0;
    }
    /**
     * Returns if this item is one of the restricted items
     * @param item
     */
    isRestrictedItem(item) {
        return this.restrictedItems.includes(item.value);
    }
    /**
     * Returns true if the sent item is partially selected
     * @param item
     * @returns
     */
    isPartiallySelected(item) {
        return item.isPartialSelected(this.selected);
    }
    /**
     * Returns true if the sent item is selected
     * @param item
     * @returns
     */
    isSelected(item) {
        return this.selected.includes(item.value);
    }
}
export { MULTI_STEP_FILTER_TYPES, MUTLI_STEP_FILTER_MODES, MULTI_STEP_FILTER_SORT, MultiStepFilterItem, MultiFilterEngine };
export default MultiStepFilterItem;
//# sourceMappingURL=MultiStepFilterItem.js.map